import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/auth/AuthContext";
import { toast } from "sonner";
import { Loader2, Save, RefreshCw, CheckCircle2, Circle } from "lucide-react";

const authHeaders = () => {
    const token = localStorage.getItem("session_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AdminMenu() {
    const [meals, setMeals] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [originalIds, setOriginalIds] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [offMenuMin, setOffMenuMin] = useState(3);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [mealsRes, weeklyRes] = await Promise.all([
                axios.get(`${API}/meals`),
                axios.get(`${API}/weekly-menu`),
            ]);
            setMeals(mealsRes.data?.meals || []);
            const ids = new Set(weeklyRes.data?.selected_meal_ids || []);
            setSelectedIds(ids);
            setOriginalIds(new Set(ids));
            setOffMenuMin(weeklyRes.data?.off_menu_min_quantity || 3);
        } catch (err) {
            toast.error("Couldn't load menu data");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const toggle = (mealId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(mealId)) next.delete(mealId);
            else next.add(mealId);
            return next;
        });
    };

    const dirty =
        selectedIds.size !== originalIds.size ||
        [...selectedIds].some((id) => !originalIds.has(id));

    const save = async () => {
        setSaving(true);
        try {
            const ids = [...selectedIds];
            await axios.patch(
                `${API}/admin/weekly-menu`,
                { selected_meal_ids: ids },
                { headers: authHeaders() },
            );
            setOriginalIds(new Set(ids));
            toast.success(
                `Weekly menu updated · ${ids.length} meal${ids.length === 1 ? "" : "s"} featured`,
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const selectAll = () => setSelectedIds(new Set(meals.map((m) => m.id)));
    const clearAll = () => setSelectedIds(new Set());

    if (loading && meals.length === 0) {
        return (
            <div className="flex items-center gap-2 text-muted2">
                <Loader2 className="animate-spin" size={16} /> Loading menu…
            </div>
        );
    }

    const selectedCount = selectedIds.size;
    const totalCount = meals.length;
    const offMenuCount = totalCount - selectedCount;

    return (
        <div data-testid="admin-menu-panel">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <p className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                    {selectedCount} of {totalCount} meals in this week's menu
                </p>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        data-testid="admin-menu-select-all"
                        onClick={selectAll}
                        className="text-xs font-bold text-forest hover:underline"
                    >
                        Select all
                    </button>
                    <span className="text-muted2">·</span>
                    <button
                        type="button"
                        data-testid="admin-menu-clear-all"
                        onClick={clearAll}
                        className="text-xs font-bold text-forest hover:underline"
                    >
                        Clear all
                    </button>
                    <span className="text-muted2">·</span>
                    <button
                        type="button"
                        data-testid="admin-menu-refresh"
                        onClick={load}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-forest hover:text-forestDark disabled:opacity-50"
                    >
                        <RefreshCw
                            size={12}
                            className={loading ? "animate-spin" : ""}
                        />
                        Refresh
                    </button>
                </div>
            </div>

            <div className="bg-cream/40 border border-mint rounded-2xl p-4 mb-6 text-sm">
                <p className="text-navy">
                    <strong>How this works:</strong> Check the meals you're
                    serving this week. Anything you leave unchecked stays
                    orderable — customers just need to take{" "}
                    <strong>{offMenuMin} of the same dish</strong> per order.
                    This rule applies to <strong>every meal</strong> (including
                    Premium plates) — Premium just means it's part of the
                    chef's specialty tier.
                </p>
            </div>

            <ul className="space-y-2.5">
                {meals.map((m) => {
                    const checked = selectedIds.has(m.id);
                    return (
                        <li
                            key={m.id}
                            data-testid={`admin-menu-row-${m.id}`}
                            className={`bg-white rounded-2xl p-4 shadow-[0_4px_18px_rgba(0,0,0,0.06)] flex items-center gap-4 cursor-pointer transition-all ${
                                checked
                                    ? "border-2 border-forest"
                                    : "border-2 border-transparent hover:border-mint"
                            }`}
                            onClick={() => toggle(m.id)}
                        >
                            <button
                                type="button"
                                data-testid={`admin-menu-toggle-${m.id}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggle(m.id);
                                }}
                                className="flex-shrink-0"
                                aria-label={
                                    checked
                                        ? `Remove ${m.name} from this week`
                                        : `Add ${m.name} to this week`
                                }
                            >
                                {checked ? (
                                    <CheckCircle2
                                        size={28}
                                        className="text-forest"
                                    />
                                ) : (
                                    <Circle
                                        size={28}
                                        className="text-muted2"
                                    />
                                )}
                            </button>
                            <img
                                src={m.image}
                                alt={m.name}
                                className="w-14 h-14 rounded-xl object-cover border border-mint flex-shrink-0"
                                draggable={false}
                            />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-bold text-navy">
                                        {m.name}
                                    </span>
                                    {m.is_premium && (
                                        <span className="px-1.5 py-0.5 rounded-full bg-navy text-gold text-[9px] font-bold uppercase tracking-widest">
                                            Premium
                                        </span>
                                    )}
                                </div>
                                <p className="mt-0.5 text-xs text-muted2">
                                    ${m.price.toFixed(2)} · {m.diet_tag}
                                </p>
                            </div>
                            <div className="text-xs text-muted2 text-right flex-shrink-0 hidden sm:block">
                                {checked ? (
                                    <span className="text-forest font-bold">
                                        On menu
                                    </span>
                                ) : (
                                    <span className="text-burnt font-bold">
                                        Min {offMenuMin}
                                    </span>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>

            <div className="mt-7 flex items-center justify-between flex-wrap gap-3 bg-white rounded-2xl p-4 shadow-[0_4px_18px_rgba(0,0,0,0.06)] sticky bottom-4">
                <p className="text-sm text-muted2">
                    <strong className="text-forest">{selectedCount}</strong>{" "}
                    on menu ·{" "}
                    <strong className="text-burnt">{offMenuCount}</strong> off
                    menu (Min {offMenuMin})
                </p>
                <button
                    type="button"
                    data-testid="admin-menu-save"
                    onClick={save}
                    disabled={saving || !dirty}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-forest hover:bg-forestDark text-white font-bold text-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : (
                        <Save size={14} />
                    )}
                    {dirty ? "Save weekly menu" : "Saved"}
                </button>
            </div>
        </div>
    );
}
